import { LegacyGraphicRef, resolveGraphicRef } from "src/config/graphicRefs";
import type { AskiDaPlayer } from "src/types/askida/lineup";
import type { Member as MatchControlMember } from "src/types/matchControl/member";
import type { Contestant, Match } from "src/types/statsperform/fixtures";
import type { Player } from "src/types/statsperform/lineup";
import type { Ranking } from "src/types/statsperform/standings";
import type { Competition } from "src/types/statsperform/tournamentCalendar";

const ALLOW_COMPETITION_CODES: string[] = ["UCL", "UCF", "UEL"];

/**
 * Sport data categories for different graphic types
 */
export const Category = {
	Standings: "Standings",
	Fixtures: "Fixtures",
	Lineup: "Lineup",
	Statistics: "Statistics",
	HockeyLineup: "HockeyLineup",
	HockeyStandings: "HockeyStandings",
	AskiDaLineup: "AskiDaLineup",
} as const;

export type Category = (typeof Category)[keyof typeof Category];

const getCategory = (graphicRef: string, selectedProperty?: Properties): Category | null => {
	if (!selectedProperty) return null;

	const legacyRef = resolveGraphicRef(graphicRef);

	if (
		selectedProperty.type === "repeater" &&
		(legacyRef === LegacyGraphicRef.LeagueTableFull ||
			legacyRef === LegacyGraphicRef.LeagueTableHalf ||
			legacyRef === LegacyGraphicRef.LowerThirdStats ||
			legacyRef === LegacyGraphicRef.SideBarStandings ||
			legacyRef === LegacyGraphicRef.LeagueTable)
	) {
		return Category.Standings;
	} else if (
		selectedProperty.type === "repeater" &&
		(legacyRef === LegacyGraphicRef.LBarResults || legacyRef === LegacyGraphicRef.LBarSchedule)
	) {
		return Category.Fixtures;
	} else if (
		selectedProperty.type === "repeater" &&
		(legacyRef === LegacyGraphicRef.LineupFull || legacyRef === LegacyGraphicRef.LineupTicker)
	) {
		return Category.Lineup;
	} else if (
		selectedProperty.type === "repeater" &&
		(legacyRef === LegacyGraphicRef.LineupTactical ||
			legacyRef === LegacyGraphicRef.BottomGoalScorers)
	) {
		return Category.HockeyLineup;
	} else if (selectedProperty.type === "repeater" && legacyRef === LegacyGraphicRef.BottomStats) {
		return Category.HockeyStandings;
	} else if (
		selectedProperty.type === "repeater" &&
		legacyRef === LegacyGraphicRef.rugbyXVGraphic
	) {
		return Category.AskiDaLineup;
	} else if (selectedProperty.type === "text" && legacyRef === LegacyGraphicRef.rugbyXVGraphic) {
		return null;
	} else if (
		selectedProperty.type === "repeater" &&
		legacyRef === LegacyGraphicRef.MatchStatistics
	) {
		return Category.Statistics;
	} else if (selectedProperty.type === "text") {
		return null;
	}

	return null;
};

const getPropertyOptionsSportData = (graphicName: string, properties: Properties[]) =>
	properties
		.filter((property) => getCategory(graphicName, property) !== null)
		.map((property) => ({
			label: property.name,
			value: property.id,
		}));

const getDomesticCompetitions = (competitions?: Competition[]) => {
	if (!competitions) return [];
	return [
		{ label: "Select a domestic league", value: "" },
		...competitions
			.filter(
				(competition) =>
					competition.competitionFormat === "Domestic league" ||
					ALLOW_COMPETITION_CODES.includes(competition.competitionCode),
			)
			.map((competition) => ({
				label: competition.name,
				value: competition.tournamentCalendar[0].id,
			}))
			.sort((a, b) => a.label.localeCompare(b.label)),
	];
};

const getPropertyValueLineupTactical = (players: MatchControlMember[]): PropertyValue[] =>
	players.map((player) => ({
		playerNumber: player.number.toString(),
		playerName: player.name,
		substitutionNumber: player.number.toString(),
		substitutionName: player.name,
	}));

const getPropertyValueBottomGoalScorers = (players: MatchControlMember[]): PropertyValue[] =>
	players.reverse().map((player) => ({
		goalScorerHome: player.name,
		goalScorerAway: player.name,
		goalMinuteHome: player.number.toString(),
		goalMinuteAway: player.number.toString(),
		goalQuarterHome: " ",
		goalQuarterAway: " ",
	}));

const getPropertyValueLeagueTable = (
	data: Ranking[],
	translations: DataProviderTranslation[],
): PropertyValue[] =>
	data.map((team) => ({
		teamRank: team.rank.toString(),
		teamName: getTranslation<Ranking>(translations, team, "contestantId", "contestantName"),
		teamLogo: `https://cdn.mxmz.io/placeholders/contestants/${team.contestantId}.png`,
		teamGames: team.matchesPlayed?.toString() || "",
		teamPoints: team.points?.toString() || "",
		teamGoalDiff: team.goaldifference?.toString() || "",
		divider: "none",
		highlight: "false",
	}));

const getPropertyValueRowsLowerThirdStats = (
	data: Ranking[],
	translations: DataProviderTranslation[],
): PropertyValue[] =>
	data.map((team) => ({
		firstColumn: team.rank.toString(),
		secondColumn: getTranslation<Ranking>(translations, team, "contestantId", "contestantName"),
		thirdColumn: team.matchesPlayed?.toString() || "",
		fourthColumn: team.points?.toString() || "",
		fifthColumn: team.goaldifference?.toString() || "",
	}));

const getPropertyValueSideBarStandings = (data: Ranking[]): PropertyValue[] =>
	data.map((team, i) => ({
		teamRank: team.rank.toString(),
		teamLogo: `https://cdn.mxmz.io/placeholders/UCL-logo-${team.contestantCode}.png`,
		teamName: team.contestantCode,
		teamPoints: team.points?.toString() || "",
		rankColor: i < 8 ? "blue" : i < 24 ? "white" : "gray",
		highlight: "false",
	}));

const getPropertyValueLeagueTableCL = (
	data: Ranking[],
	translations: DataProviderTranslation[],
): PropertyValue[] =>
	data.map((team) => ({
		teamRank: team.rank.toString(),
		teamName: getTranslation<Ranking>(translations, team, "contestantId", "contestantName"),
		teamAsterix: "",
		teamPoints: team.points?.toString() || "",
		highlight: "false",
	}));

const getPropertyValueFixtures = (
	data: Match[],
	translations: DataProviderTranslation[],
): PropertyValue[] => {
	const result: PropertyValue[] = [];
	const matchDays = new Map<string, Match[]>();

	for (const match of data) {
		const matchDate = match.matchInfo.date;
		const matchDay = matchDays.get(matchDate) || [];
		matchDay.push(match);
		matchDays.set(matchDate, matchDay);
	}

	const entries = Array.from(matchDays.entries());
	entries.sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime());

	for (const [date, matches] of entries) {
		result.push({
			sectionTitle: new Date(date).toLocaleDateString(),
			items: matches.map((match) => ({
				itemHome: getTranslation<Contestant>(
					translations,
					match.matchInfo.contestant[0],
					"id",
					"name",
				),
				itemAway: getTranslation<Contestant>(
					translations,
					match.matchInfo.contestant[1],
					"id",
					"name",
				),
				programChannel: "none",
			})),
		});
	}

	return result;
};

const getPropertyValueResults = (
	data: Match[],
	translations: DataProviderTranslation[],
): PropertyValue[] =>
	data.map((match) => {
		const homeTeam = match.matchInfo.contestant[0];
		const awayTeam = match.matchInfo.contestant[1];

		const homeScore = match.liveData?.matchDetails?.scores?.total?.home ?? " ";
		const awayScore = match.liveData?.matchDetails?.scores?.total?.away ?? " ";
		const score = `${homeScore}-${awayScore}`;

		return {
			resultScore: score,
			resultHomeName: getTranslation<Contestant>(translations, homeTeam, "id", "name"),
			resultHomeScore: homeScore.toString(),
			resultHomeLogo: `https://cdn.mxmz.io/placeholders/contestants/${homeTeam.id}.png`,
			resultAwayName: getTranslation<Contestant>(translations, awayTeam, "id", "name"),
			resultAwayScore: awayScore.toString(),
			resultAwayLogo: `https://cdn.mxmz.io/placeholders/contestants/${awayTeam.id}.png`,
		};
	});

const getPropertyValueLineupFullCL = (data: Player[]): PropertyValue[] =>
	data.map((player) => ({
		playerNumber: player.shirtNumber.toString(),
		playerName: player.shortLastName ?? player.lastName ?? "",
		playerCard: "None",
	}));

const getPropertyValueAskiDaLineup = (data: AskiDaPlayer[]): PropertyValue[] =>
	data.map((player, index) => ({
		playerId: (index + 1).toString(), // Sequential numbering from 1
		playerName: player.firstName,
	}));

const getPropertyValues = (
	property: PlaylistGraphic,
	data: Ranking[] | Match[] | Player[] | MatchControlMember[] | AskiDaPlayer[],
	translations: DataProviderTranslation[],
) => {
	const legacyRef = resolveGraphicRef(property.graphic.ref);
	switch (legacyRef) {
		case LegacyGraphicRef.LBarResults:
			return getPropertyValueResults(data as Match[], translations);
		case LegacyGraphicRef.LBarSchedule:
			return getPropertyValueFixtures(data as Match[], translations);
		case LegacyGraphicRef.LeagueTableFull:
		case LegacyGraphicRef.LeagueTableHalf:
			return getPropertyValueLeagueTable(data as Ranking[], translations);
		case LegacyGraphicRef.LowerThirdStats:
			return getPropertyValueRowsLowerThirdStats(data as Ranking[], translations);
		case LegacyGraphicRef.SideBarStandings:
			return getPropertyValueSideBarStandings(data as Ranking[]);
		case LegacyGraphicRef.LeagueTable:
			return getPropertyValueLeagueTableCL(data as Ranking[], translations);
		case LegacyGraphicRef.LineupFull:
		case LegacyGraphicRef.LineupTicker:
			return getPropertyValueLineupFullCL(data as Player[]);
		case LegacyGraphicRef.LineupTactical:
			return getPropertyValueLineupTactical(data as MatchControlMember[]);
		case LegacyGraphicRef.BottomGoalScorers:
			return getPropertyValueBottomGoalScorers(data as MatchControlMember[]);
		case LegacyGraphicRef.rugbyXVGraphic:
			return getPropertyValueAskiDaLineup(data as AskiDaPlayer[]);
		default:
			return [];
	}
};

const getTranslation = <T>(
	translations: DataProviderTranslation[],
	data: T,
	id: keyof T,
	property: keyof T,
): string | number | PropertyValue[] => {
	const fallbackValue = data[property] as string | number | object;

	// Check if the fallback value is not a primitive type, and return an empty string if it’s an object
	if (typeof fallbackValue === "object") {
		return "";
	}

	if (!translations.length) {
		return fallbackValue ?? "";
	}

	// Find the translation matching the given entityId and property
	const foundTranslation = translations.find(
		(t) => t.entityId === data[id] && t.property === property,
	);

	// Return the found translation value if available, otherwise return the fallback or empty string
	return foundTranslation?.value ?? fallbackValue ?? "";
};

export { getCategory, getDomesticCompetitions, getPropertyOptionsSportData, getPropertyValues };
